import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type AttachmentType = "image" | "video" | "document" | "link";

export interface Attachment {
  id: string;
  announcement_id: string;
  attachment_type: AttachmentType;
  url: string;
  title: string | null;
  file_name: string | null;
  thumbnail_url: string | null;
  created_at: string;
}

export interface PendingAttachment {
  id: string;
  type: AttachmentType;
  url: string;
  title?: string;
  fileName?: string;
  file?: File;
}

export function useAnnouncementAttachments(announcementId?: string) {
  const queryClient = useQueryClient();

  const { data: attachments, isLoading } = useQuery({
    queryKey: ["announcement_attachments", announcementId],
    queryFn: async () => {
      if (!announcementId) return [];
      const { data, error } = await supabase
        .from("announcement_attachments")
        .select("*")
        .eq("announcement_id", announcementId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as Attachment[];
    },
    enabled: !!announcementId,
  });

  /**
   * Uploads a file and returns the storage PATH (not full URL)
   */
  const uploadFile = async (file: File, type: "image" | "document"): Promise<string> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${type}s/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("announcements-media")
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    // Return the path, not the full URL
    return filePath;
  };

  const addAttachments = useMutation({
    mutationFn: async ({
      announcementId,
      attachments,
    }: {
      announcementId: string;
      attachments: PendingAttachment[];
    }) => {
      const uploadedAttachments = await Promise.all(
        attachments.map(async (attachment) => {
          let url = attachment.url;

          // Upload file if it exists
          if (attachment.file) {
            url = await uploadFile(attachment.file, attachment.type as "image" | "document");
          }

          return {
            announcement_id: announcementId,
            attachment_type: attachment.type,
            url,
            title: attachment.title || null,
            file_name: attachment.fileName || null,
          };
        })
      );

      const { error } = await supabase
        .from("announcement_attachments")
        .insert(uploadedAttachments);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcement_attachments"] });
    },
    onError: (error) => {
      toast.error("Failed to upload attachments: " + error.message);
    },
  });

  const deleteAttachment = useMutation({
    mutationFn: async (attachmentId: string) => {
      const { error } = await supabase
        .from("announcement_attachments")
        .delete()
        .eq("id", attachmentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcement_attachments"] });
      toast.success("Attachment deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete attachment: " + error.message);
    },
  });

  return {
    attachments,
    isLoading,
    addAttachments,
    deleteAttachment,
    uploadFile,
  };
}

// Utility function to convert video URLs to embed URLs
export function getVideoEmbedUrl(url: string): string | null {
  // YouTube
  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }

  // Vimeo
  const vimeoMatch = url.match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  return null;
}

export function getVideoThumbnail(url: string): string | null {
  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (youtubeMatch) {
    return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
  }
  return null;
}
